import type { Cart, ChargeLine } from './cart'
import type { CartLineSpecification } from './cart-line-specification'
import type { PaymentSelection, ShippingAddress, ShippingMethod } from './checkout'
import type { DeliveryPromise } from './delivery-promise'
import { invariant } from './errors'
import {
  type LineItem,
  type OrderEligibleLineItem,
  isOrderEligible,
  toOrderEligible,
} from './line-item'
import { type ActivityEntry, type CustomsInfo, makeCustomsInfo } from './order-activity-customs'
import { deriveOrderEta, deriveOrderName, derivePaymentDisplayLabel } from './order-derivations'
import { type OrderStatus, isOrderStatus } from './order-status'
import type { Price } from './price'
import type { Quantity } from './quantity'
import type { RequestedService } from './requested-service'
import type { SourceableRef } from './sourceable'

/**
 * Order aggregate + place-order flow (WS-5, UID-61) — the checkout step that turns a
 * mixed-readiness {@link Cart} (UID-58) into a placed {@link Order}. This is the
 * framework-agnostic domain heart, the projection of the FROZEN shared contract
 * `Order` / `OrderLine` / `OrderStatus` / `OrderLineStatus` (api-specs, ADR-0025) —
 * consumed here, never redefined on the wire. Server-authoritative tenancy
 * (`accountId` server-derived, C2), persistence, and transport all live in the BFF
 * `order` module; this file holds only pure, immutable logic.
 *
 * Two invariants make illegal states unrepresentable (software-design-patterns):
 *   1. **The as-ordered baseline (ADR-0004).** An {@link OrderLine} is ALWAYS priced
 *      AND promised — the `ready`-line invariant made non-optional. A line with a
 *      missing Price or DeliveryPromise can NEVER become an OrderLine: the one
 *      transition point {@link makeOrderLine} routes through `toOrderEligible`, which
 *      throws otherwise.
 *   2. **The readiness split (DM§9 a′).** Placing an order takes ONLY the cart's
 *      `ready` lines (both Price and DeliveryPromise) as OrderLines; `pending` lines
 *      are left behind (they remain in the cart / become a Quote — never deleted or
 *      mangled here). An order with no lines is rejected ({@link OrderError}), and
 *      {@link makeOrder} refuses an empty line set.
 *
 * `netsuiteSalesOrderId` / `salesforceOpportunityId` are READ-ONLY inbound projection
 * fields (DM§17 / §19.2) filled by UID-62's stub — left `null` here, out of scope.
 */

/**
 * Per-line fulfillment status (mirror of the frozen contract `OrderLineStatus`,
 * DM§9/§10). A new line starts `placed`; the richer flow is UID-63 tracking.
 */
export const ORDER_LINE_STATUSES = [
  'placed',
  'in_production',
  'shipped',
  'delivered',
  'cancelled',
] as const

export type OrderLineStatus = (typeof ORDER_LINE_STATUSES)[number]

export function isOrderLineStatus(value: unknown): value is OrderLineStatus {
  return typeof value === 'string' && (ORDER_LINE_STATUSES as readonly string[]).includes(value)
}

/**
 * A committed order line (DM§9 a′). Field-for-field the frozen contract `OrderLine`:
 * always priced AND promised — the as-ordered baseline frozen at placement. Carries a
 * per-line {@link OrderLineStatus} for partial-fulfillment tracking (UID-63).
 */
export interface OrderLine {
  readonly id: string
  readonly sourceable: SourceableRef
  readonly quantity: Quantity
  readonly price: Price
  readonly deliveryPromise: DeliveryPromise
  readonly status: OrderLineStatus
  /**
   * Human-facing part labels captured as an AS-PLACED snapshot at placement (UID-98 PR-A,
   * D3) from the settling cart line's `displayName`/`partNumber` (the 3005 cart columns),
   * persisted on `"order".order_line` (migration 3007). `displayName` is the friendly part
   * name (e.g. "Deep-groove ball bearing 6800ZZ") and `partNumber` the catalog SKU (e.g.
   * "B6800ZZ"); the order-detail per-line render (S6/UID-100) prefers them over the raw
   * `sourceable.id` UUID. Both OPTIONAL — absent on a line settled from an unlabelled cart
   * line and on every pre-3007 order; NOT part of the ADR-0004 as-ordered baseline invariant.
   */
  readonly displayName?: string
  readonly partNumber?: string
  /**
   * Originating quote/RFQ lineage captured as an AS-PLACED snapshot at placement
   * (UID-212) from the settling cart line's `sourceRfqId`/`sourceQuoteId` (the 3000
   * cart columns), persisted on `"order".order_line` (migration 3012). Line-level —
   * an order can span lines priced from different quotes — so the order-detail
   * "Reference quote #" is derived ACROSS the order's lines at render time. Both
   * OPTIONAL/nullable: a catalog-added line has no originating quote, and every
   * pre-3012 order reads absent. Copied BEFORE settlement removes the cart line
   * (after settlement the lineage is unrecoverable); NOT part of the ADR-0004
   * as-ordered baseline invariant.
   */
  readonly sourceRfqId?: string | null
  readonly sourceQuoteId?: string | null
  /**
   * Originating BOM lineage captured as an AS-PLACED snapshot at placement (UID-326,
   * ADR-0108) from the settling cart line's `sourceBomId` (the 3016 cart column),
   * persisted on `"order".order_line` (migration 3016). Line-level — an order can mix
   * BOM-originated and non-BOM lines — so the BOM detail "Order History" tab filters an
   * order's lines to those whose `sourceBomId === :bomId`. OPTIONAL/nullable: a
   * catalog/quote/reorder line has no originating BOM, and every pre-3016 order reads
   * absent. Copied BEFORE settlement removes the cart line (after settlement the lineage
   * is unrecoverable); NOT part of the ADR-0004 as-ordered baseline invariant.
   */
  readonly sourceBomId?: string | null
  /**
   * The AS-PLACED manufacturing configuration (UID-512, ADR-0161 D3), copied verbatim
   * from the settling cart line's `specification` (the 3020 cart column) and persisted
   * on `"order".order_line.specification` (migration 3021).
   *
   * WHY IT EXISTS. Before this field the buyer's configuration existed ONLY in memory
   * inside `placeOrderExclusive`: `makeOrderLine` did not copy it and the `cart.removeLine()`
   * settle loop then deleted the cart line, so a placed order carried no record of the
   * manufacturing configuration it was placed with. ADR-0161 D3 rules that snapshot a
   * PRECONDITION for the fail-open library writer — a dropped library write is only
   * acceptable because it is REPLAYABLE from this column. The two ship together.
   *
   * This is a DOMAIN-type field only. The WIRE `OrderLine` in the `api-specs` submodule is
   * FROZEN (ADR-0025/ADR-0030) and does not gain a property; the field is surfaced to
   * clients as the `lineSpecifications` SIDECAR ARRAY on `OrderDetail` (ADR-0150 §2), the
   * way `CartViewWithLineSpecifications` already does it on the cart.
   *
   * OPTIONAL: a line the buyer never configured has none, and every pre-3021 order reads
   * absent. `finish: null` inside it is a VALUE ("as machined", ADR-0117 D4), never an
   * absence. NOT part of the ADR-0004 as-ordered baseline invariant.
   */
  readonly specification?: CartLineSpecification
}

/**
 * Fulfillment shipment status (mirror of the frozen contract `Shipment.status` and the
 * migration 3008 `"order".shipment.status` CHECK, UID-98 B3 / ADR-0045). A shipment starts
 * `pending`; the richer flow is fulfillment-projection (a future writer — seeded in PR-B).
 */
export const SHIPMENT_STATUSES = [
  'pending',
  'in_production',
  'shipped',
  'in_transit',
  'delivered',
  'cancelled',
] as const

export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number]

export function isShipmentStatus(value: unknown): value is ShipmentStatus {
  return typeof value === 'string' && (SHIPMENT_STATUSES as readonly string[]).includes(value)
}

/**
 * Document kinds carried on the order-detail Documents surface (UID-98 B3 / ADR-0045,
 * mirror of the contract `OrderDocument.kind` and the migration 3009 CHECK). Feeds D3/D4:
 * `order_confirmation` (one per order, order-level — line NULL), `annotated_pdf` (one per
 * part/line), `quality_doc` (zero-or-more per part/line).
 */
export const ORDER_DOCUMENT_KINDS = ['order_confirmation', 'annotated_pdf', 'quality_doc'] as const

export type OrderDocumentKind = (typeof ORDER_DOCUMENT_KINDS)[number]

export function isOrderDocumentKind(value: unknown): value is OrderDocumentKind {
  return typeof value === 'string' && (ORDER_DOCUMENT_KINDS as readonly string[]).includes(value)
}

/**
 * One order line a shipment bundles (UID-98 B3 / ADR-0045) — the projection of a
 * `"order".shipment_line` membership row. Kept an object (not a bare id) so it can grow
 * shipped-qty / backorder fields later without a breaking contract change (ADR-0045 §2.2).
 */
export interface ShipmentLine {
  readonly orderLineId: string
}

/**
 * A fulfillment shipment grouping ≥1 of an order's lines under a carrier / tracking / ETA /
 * status (UID-98 B3 / ADR-0045, feeds D5). The projection of a `"order".shipment` row plus
 * its `shipment_line` members. `sequenceIndex` is the 1-based "Shipment N of M" ordinal.
 * Every carrier/tracking/date/signature field is NULLABLE (demo SOs frequently carry none).
 */
export interface Shipment {
  readonly id: string
  readonly sequenceIndex: number
  readonly carrier: string | null
  readonly serviceLevel: string | null
  readonly trackingNumber: string | null
  readonly estShipDate: string | null
  readonly shippedAt: string | null
  readonly deliveredAt: string | null
  readonly signedBy: string | null
  readonly status: ShipmentStatus
  readonly lines: readonly ShipmentLine[]
}

/**
 * A reference/metadata row for an order document (UID-98 B3 / ADR-0045, feeds D3/D4) — the
 * projection of a `"order".order_document` row. `reference` is an opaque pointer at a seeded
 * demo fixture, NEVER a generated file / S3 key (ADR-0045 §2.1). `orderLineId` is NULL for
 * the order-level `order_confirmation` and REQUIRED for the two per-part kinds — the shape
 * invariant the migration 3009 `order_document_line_shape` CHECK enforces in the DB.
 */
export interface OrderDocument {
  readonly id: string
  readonly kind: OrderDocumentKind
  readonly label: string
  readonly reference: string
  readonly contentType: string | null
  readonly orderLineId: string | null
}

/**
 * Build a {@link ShipmentLine} (a member-line reference), enforcing a non-empty id.
 */
export function makeShipmentLine(input: { orderLineId: string }): ShipmentLine {
  invariant(
    typeof input.orderLineId === 'string' && input.orderLineId.length > 0,
    'ShipmentLine.orderLineId is required',
  )
  return Object.freeze({ orderLineId: input.orderLineId })
}

/**
 * Build a {@link Shipment}. Nullable carrier/tracking/date/signature fields default to
 * `null`, `status` to `pending`, and `lines` to an empty (frozen) array — the "nothing
 * grouped yet" shape. Mirrors the migration 3008 invariants: `sequenceIndex` is a 1-based
 * integer and `status` must be in the closed {@link SHIPMENT_STATUSES} set.
 */
export function makeShipment(input: {
  id: string
  sequenceIndex: number
  carrier?: string | null
  serviceLevel?: string | null
  trackingNumber?: string | null
  estShipDate?: string | null
  shippedAt?: string | null
  deliveredAt?: string | null
  signedBy?: string | null
  status?: ShipmentStatus
  lines?: readonly { orderLineId: string }[]
}): Shipment {
  invariant(typeof input.id === 'string' && input.id.length > 0, 'Shipment.id is required')
  invariant(
    Number.isInteger(input.sequenceIndex) && input.sequenceIndex >= 1,
    'Shipment.sequenceIndex must be an integer >= 1',
  )
  const status = input.status ?? 'pending'
  invariant(isShipmentStatus(status), `Shipment.status "${status}" is not a valid ShipmentStatus`)
  return Object.freeze({
    id: input.id,
    sequenceIndex: input.sequenceIndex,
    carrier: input.carrier ?? null,
    serviceLevel: input.serviceLevel ?? null,
    trackingNumber: input.trackingNumber ?? null,
    estShipDate: input.estShipDate ?? null,
    shippedAt: input.shippedAt ?? null,
    deliveredAt: input.deliveredAt ?? null,
    signedBy: input.signedBy ?? null,
    status,
    lines: Object.freeze((input.lines ?? []).map(makeShipmentLine)),
  })
}

/**
 * Build the ONE initial {@link Shipment} an order gets at placement (UID-224 item 1). A placed
 * order — single OR split-PO — always carries exactly one `pending` shipment (`sequenceIndex: 1`)
 * whose member lines cover ALL of the order's lines, so the order-detail Shipments surface renders
 * a real shipment from the moment of placement instead of an empty state. This is deliberately the
 * ONLY shipment created at placement: there is no auto-advance / reconciler / split in this ticket,
 * so every lifecycle stamp (`trackingNumber`/`estShipDate`/`shippedAt`/`deliveredAt`/`signedBy`)
 * stays `null` — a future fulfillment writer fills them in.
 *
 * The shipment INHERITS the order's shipping method (passed explicitly — the resolved
 * {@link ShippingMethod} snapshot, i.e. `order.shippingMethod`): for an `own_account` method the
 * `carrier` is carried onto the shipment; `serviceLevel` is left `null` (the current ShippingMethod
 * shape carries no service-level / name field, and the FE derives the own-account label from
 * `order.shippingMethod`). A `prepay` or absent (`null`) method degrades to a null carrier + null
 * serviceLevel — the "nothing to inherit" shape, never an `undefined`.
 *
 * PURE: no I/O, no clock, no id minting. The shipment id is derived deterministically from the
 * order id (one shipment per order at placement), so re-seeding replaces rather than duplicates —
 * mirroring how the demo seed's {@link import('../../apps/api/src/modules/order/demo-order-seed')}
 * `buildDemoFulfillment` keys shipment member lines off the order's REAL persisted line ids.
 */
export function buildInitialShipment(
  order: Order,
  shippingMethod: ShippingMethod | null,
): Shipment {
  const carrier = shippingMethod?.kind === 'own_account' ? shippingMethod.carrier : null
  // UID-212 follow-up: a prepay method now carries the buyer's selected tier + est delivery
  // (captured at checkout resolution), so inherit them onto the shipment — the Shipments tab shows
  // "Ground"/"Overnight" + an est ship date on a live order, not just seeded demo orders. own_account
  // carries neither (its label is derived from the carrier/accountRef snapshot on order.shippingMethod).
  const isPrepay = shippingMethod?.kind === 'prepay'
  return makeShipment({
    id: order.id,
    sequenceIndex: 1,
    status: 'pending',
    carrier,
    serviceLevel: isPrepay ? (shippingMethod.serviceLevel ?? null) : null,
    estShipDate: isPrepay ? (shippingMethod.estDeliveryDate ?? null) : null,
    // Cover EVERY order line: one ShipmentLine per OrderLine, keyed off the line id.
    lines: order.lines.map((line) => ({ orderLineId: line.id })),
  })
}

/**
 * Build the OPEN, editable customs record attached to EVERY order at placement (UID-232).
 * Product decision: the Customs tab shows on every order with an editable form, not just import
 * orders — reversing the earlier import-only rule (`order.customs != null` used to gate the tab,
 * and only the demo seed ever wrote a record). This is the placement counterpart to the demo
 * seed's `buildDemoCustoms`.
 *
 * The record starts in the `open` submission state so the buyer can fill it via the existing
 * `submitCustoms` flow (the 48h edit window is derived client-side from `order.placedAt`). The
 * customer-entered fields (`partDescription` / `parentProductEndUse`) and the Fictiv-assigned
 * fields (`hsCode` / `countryOfOrigin`) are left `null` — placement carries no origin/HS data,
 * and, unlike the demo seed, we do not fabricate any. `classification` defaults to `production`
 * (a placed order is a real production order, not a prototype).
 *
 * ⚠️ `incoterms`/`importerOfRecord` on the placement record are NON-AUTHORITATIVE defaults, NOT a
 * determination of shipping terms (code-review W1). The authoritative DAP-vs-DDP / importer-of-
 * record answer is DERIVED from the parts' country of ORIGIN — which is unknown at placement (all
 * line origins are `null`). Two consequences:
 *   - `importerOfRecord` is `null` here (undetermined), NOT `'Fictiv'`: at placement we do not yet
 *     know whether Fictiv is the importer of record, so we do not assert it.
 *   - `incoterms` is `'DDP'` only because the contract enum ({@link CUSTOMS_INCOTERMS}) currently
 *     admits no other value — it is a placeholder, not a claim that duties are Fictiv-paid.
 * Any consumer (UI, and a future ERP/NetSuite/logistics export) MUST derive the effective terms
 * from country of origin (see the web `resolveCustomsOriginState`), NOT read these fields verbatim.
 * The demo seed's `buildDemoCustoms` sets a real `importerOfRecord` for its designated IMPORT
 * orders (which DO have a known non-US origin) — that path is unaffected.
 *
 * PURE: no I/O, no clock, no id minting — one customs line per order line, keyed off the order's
 * REAL persisted line ids (composite-FK safe once the order is saved), mirroring
 * {@link buildInitialShipment}.
 */
export function buildInitialCustoms(order: Order): CustomsInfo {
  return makeCustomsInfo({
    classification: 'production',
    incoterms: 'DDP',
    // Undetermined at placement (origin unknown) — do NOT assert Fictiv as importer of record here;
    // the terms are derived from country of origin downstream (code-review W1).
    importerOfRecord: null,
    submissionStatus: 'open',
    lines: order.lines.map((line) => ({
      orderLineId: line.id,
      partDescription: null,
      parentProductEndUse: null,
      hsCode: null,
      countryOfOrigin: null,
    })),
  })
}

/**
 * Build an {@link OrderDocument}, enforcing the D3/D4 line-shape invariant (mirroring the
 * migration 3009 `order_document_line_shape` CHECK): an `order_confirmation` is order-level
 * and MUST NOT carry an `orderLineId`; the two per-part kinds (`annotated_pdf`/`quality_doc`)
 * MUST carry one. `contentType` defaults to `null`.
 */
export function makeOrderDocument(input: {
  id: string
  kind: OrderDocumentKind
  label: string
  reference: string
  contentType?: string | null
  orderLineId?: string | null
}): OrderDocument {
  invariant(typeof input.id === 'string' && input.id.length > 0, 'OrderDocument.id is required')
  invariant(
    isOrderDocumentKind(input.kind),
    `OrderDocument.kind "${input.kind}" is not a valid OrderDocumentKind`,
  )
  invariant(
    typeof input.label === 'string' && input.label.length > 0,
    'OrderDocument.label is required',
  )
  invariant(
    typeof input.reference === 'string' && input.reference.length > 0,
    'OrderDocument.reference is required',
  )
  const orderLineId = input.orderLineId ?? null
  if (input.kind === 'order_confirmation') {
    invariant(
      orderLineId === null,
      'OrderDocument of kind order_confirmation is order-level and must not carry an orderLineId',
    )
  } else {
    invariant(
      typeof orderLineId === 'string' && orderLineId.length > 0,
      `OrderDocument of kind ${input.kind} requires an orderLineId (per-part document)`,
    )
  }
  return Object.freeze({
    id: input.id,
    kind: input.kind,
    label: input.label,
    reference: input.reference,
    contentType: input.contentType ?? null,
    orderLineId,
  })
}

/**
 * A placed order (DM§9 a′). Field-for-field the frozen contract `Order`. `status` is
 * the canonical customer-facing {@link OrderStatus} (a read-model projection, never
 * mutated directly); `netsuiteSalesOrderId` / `salesforceOpportunityId` are read-only
 * inbound projections filled by UID-62 (null at placement).
 */
export interface Order {
  readonly id: string
  readonly accountId: string
  readonly status: OrderStatus
  readonly lines: readonly OrderLine[]
  readonly poReference: string | null
  readonly netsuiteSalesOrderId: string | null
  readonly salesforceOpportunityId: string | null
  readonly placedAt: string | null
  readonly charges: readonly ChargeLine[]
  readonly createdAt?: string
  /**
   * Frozen checkout-placement selections (UID-60, ADR-0038 §1) — the buyer's
   * placement-time shipping/payment/terms snapshot, captured 1:1 on the aggregate
   * header exactly as ADR-0004 freezes price/promise. All NULLABLE: a pre-UID-60
   * order (migration 3001 / a UID-62 projection row) carries `null` for each, and the
   * "a checkout-placed order always has shipping+payment+terms" invariant is enforced at
   * the APPLICATION layer (the CheckoutRequest validation + the OrderService precheck),
   * never a DB `NOT NULL` (back-compat, ADR-0038 §4).
   */
  readonly shippingAddress: ShippingAddress | null
  readonly shippingMethod: ShippingMethod | null
  readonly payment: PaymentSelection | null
  /** ISO-8601 (timestamptz); SERVER-stamped at placement acceptance, never client-supplied. */
  readonly termsAcceptedAt: string | null
  /**
   * Fulfillment shipments grouping this order's lines (UID-98 B3 / ADR-0045, feeds D5). A
   * DETAIL-only projection assembled by the repository from `"order".shipment` + its
   * `shipment_line` members; `[]` when nothing has shipped. Placement does NOT create
   * shipments (fulfillment is downstream/seeded) — `placeOrder`/`makeOrder` default to `[]`.
   */
  readonly shipments: readonly Shipment[]
  /**
   * Reference/metadata rows for the order-confirmation doc, per-part annotated PDFs, and
   * quality docs (UID-98 B3 / ADR-0045, feeds D3/D4). A DETAIL-only projection assembled by
   * the repository from `"order".order_document`; `[]` when the order has none. Points at
   * seeded demo fixtures, never generated files.
   */
  readonly documents: readonly OrderDocument[]
  /**
   * Chronological activity entries scoped to this order (UID-121 S7 / ADR-0046, feeds the
   * order-detail "View activity log" surface). The order-scoped subset of the GENERIC,
   * cross-aggregate {@link ActivityEntry} feed (`subjectType` ∈ {order, order_line},
   * `subjectId` = this order / its lines). A DETAIL-only projection assembled by the repository
   * from `"order".activity_entry`; `[]` when the order has none. Placement does NOT write
   * activity (seeded / a future writer) — `placeOrder`/`makeOrder` default to `[]`.
   */
  readonly activityLog: readonly ActivityEntry[]
  /**
   * Customs/import information for this order (UID-121 S7 / ADR-0046, feeds the order-detail
   * Customs information tab). A DETAIL-only projection assembled by the repository from
   * `"order".order_customs` + its `order_customs_line` members; `null` when the order has no
   * customs record. Placement does NOT write customs — `placeOrder`/`makeOrder` default to `null`.
   */
  readonly customs: CustomsInfo | null
  /**
   * The buyer's requested value-added services AS SNAPSHOTTED at placement (UID-428, ADR-0127).
   * A frozen, point-in-time copy of the settling cart's {@link Cart.requestedServices} — the
   * structured counterpart to the human-readable `requestedServicesNote(...)` activity entry
   * (which is additive and stays). At most one service per {@link RequestedService.type}, carried
   * verbatim (server-minted ids and all). REQUIRED-but-defaulted: `placeOrder`/`makeOrder` default
   * to a frozen `[]` (an order placed from a cart with no services carries none), matching the
   * B3/UID-121 detail-surface posture. Consumed by `reorder` to replay the services onto the new
   * cart. In-memory persisted this ticket; the pg column is wired by the data-engineer next.
   */
  readonly requestedServices: readonly RequestedService[]
}

/**
 * Typed order failure the transport maps to an HTTP status + neutral problem body.
 * - `no_ready_lines` — the cart holds nothing placeable (→ 422).
 * - `already_placed`  — a concurrent/double-submitted placement lost the race for the
 *   same cart lines; the winning Order already exists (→ 409). The durable pg repository
 *   raises this when the DEFECT-1 partial unique index (`order_line_one_settlement_per_
 *   cart_line`, migration 3001) rejects the loser; see `TenantOrderRepository.save`.
 */
/**
 * - `terms_not_accepted` — the placement's `CheckoutRequest.termsAccepted` was not
 *   strictly `true` (→ 422). The server stamps `termsAcceptedAt` only on the accepted path.
 * - `payment_method_not_eligible` — the selected `payment.kind` is not in the account's
 *   server-derived allowed methods (the authoritative C18 transaction-time check, ADR-0038
 *   §3 / ADR-0024; → 422). Neutral body — no eligibility detail is leaked.
 * - `invalid_selection` — the placement's id-based checkout selection could not be resolved
 *   to a placement snapshot (Stage-2a): an unknown/ambiguous/missing ship-to address,
 *   shipping method, or saved payment method (→ 422). Neutral body — the request named a
 *   selection the server cannot honor; it is a bad request against server-owned demo state,
 *   never an eligibility leak.
 * - `invalid_grouping` — the split-PO batch placement's client-supplied `groups[]` do NOT
 *   partition EXACTLY the cart's READY lines (UID-97 K1, ADR-0044 §1): a ready line missing
 *   from every group, a line placed in two groups, an unknown/pending/non-ready line id, an
 *   empty group, or a malformed per-group `poReference` (→ 422). This makes "which lines
 *   silently dropped" an unrepresentable state — the batch fails closed BEFORE any Order is
 *   minted. Neutral body — a bad request against server-owned cart state, no data leaked.
 */
export type OrderErrorCode =
  | 'no_ready_lines'
  | 'already_placed'
  | 'terms_not_accepted'
  | 'payment_method_not_eligible'
  | 'invalid_selection'
  | 'invalid_grouping'

export class OrderError extends Error {
  constructor(
    readonly code: OrderErrorCode,
    message: string,
  ) {
    super(message)
    this.name = 'OrderError'
  }
}

/**
 * Build an {@link OrderLine} from a cart line, ENFORCING the as-ordered baseline
 * invariant: the line must hold both a Price and a DeliveryPromise, else
 * `toOrderEligible` throws (ADR-0004). Status defaults to `placed`.
 */
export function makeOrderLine(input: {
  id: string
  line: LineItem
  status?: OrderLineStatus
}): OrderLine {
  invariant(typeof input.id === 'string' && input.id.length > 0, 'OrderLine.id is required')
  const status = input.status ?? 'placed'
  invariant(
    isOrderLineStatus(status),
    `OrderLine.status "${status}" is not a valid OrderLineStatus`,
  )
  const eligible = toOrderEligible(input.line)
  return Object.freeze({
    id: input.id,
    sourceable: eligible.sourceable,
    quantity: eligible.quantity,
    price: eligible.price,
    deliveryPromise: eligible.deliveryPromise,
    status,
    // As-placed part-label snapshot (UID-98 D3), carried verbatim from the settling cart
    // line; omitted entirely when the line had no label so pre-3007 orders stay byte-identical.
    ...(eligible.displayName !== undefined ? { displayName: eligible.displayName } : {}),
    ...(eligible.partNumber !== undefined ? { partNumber: eligible.partNumber } : {}),
    // As-placed quote/RFQ lineage snapshot (UID-212), copied verbatim from the settling
    // cart line BEFORE settlement removes it; omitted when the line had no lineage so
    // catalog-only and pre-3012 orders stay byte-identical.
    ...(eligible.sourceRfqId !== undefined ? { sourceRfqId: eligible.sourceRfqId } : {}),
    ...(eligible.sourceQuoteId !== undefined ? { sourceQuoteId: eligible.sourceQuoteId } : {}),
    // As-placed BOM lineage snapshot (UID-326, ADR-0108), copied verbatim from the settling
    // cart line BEFORE settlement removes it; omitted when the line had no BOM origin so
    // non-BOM and pre-3016 orders stay byte-identical.
    ...(eligible.sourceBomId !== undefined ? { sourceBomId: eligible.sourceBomId } : {}),
    // As-placed manufacturing-configuration snapshot (UID-512, ADR-0161 D3), copied
    // verbatim from the settling cart line BEFORE settlement deletes it — after the
    // `cart.removeLine()` settle loop the configuration is unrecoverable, which is
    // precisely the provenance defect this closes. Omitted entirely when the line was
    // never configured, so pre-3021 orders stay byte-identical and "never configured"
    // stays distinguishable from "configured to nothing".
    ...(eligible.specification !== undefined ? { specification: eligible.specification } : {}),
  })
}

export function makeOrder(input: {
  id: string
  accountId: string
  status?: OrderStatus
  lines: readonly OrderLine[]
  poReference?: string | null
  netsuiteSalesOrderId?: string | null
  salesforceOpportunityId?: string | null
  placedAt?: string | null
  charges?: readonly ChargeLine[]
  createdAt?: string
  shippingAddress?: ShippingAddress | null
  shippingMethod?: ShippingMethod | null
  payment?: PaymentSelection | null
  termsAcceptedAt?: string | null
  shipments?: readonly Shipment[]
  documents?: readonly OrderDocument[]
  activityLog?: readonly ActivityEntry[]
  customs?: CustomsInfo | null
  requestedServices?: readonly RequestedService[]
}): Order {
  invariant(typeof input.id === 'string' && input.id.length > 0, 'Order.id is required')
  invariant(
    typeof input.accountId === 'string' && input.accountId.length > 0,
    'Order.accountId is required',
  )
  invariant(
    Array.isArray(input.lines) && input.lines.length > 0,
    'An Order must have at least one OrderLine (nothing to place)',
  )
  const status = input.status ?? 'placed'
  invariant(isOrderStatus(status), `Order.status "${status}" is not a valid OrderStatus`)
  return Object.freeze({
    id: input.id,
    accountId: input.accountId,
    status,
    lines: Object.freeze([...input.lines]),
    poReference: input.poReference ?? null,
    netsuiteSalesOrderId: input.netsuiteSalesOrderId ?? null,
    salesforceOpportunityId: input.salesforceOpportunityId ?? null,
    placedAt: input.placedAt ?? null,
    charges: Object.freeze([...(input.charges ?? [])]),
    shippingAddress: input.shippingAddress ?? null,
    shippingMethod: input.shippingMethod ?? null,
    payment: input.payment ?? null,
    termsAcceptedAt: input.termsAcceptedAt ?? null,
    // Fulfillment surfaces (UID-98 B3, ADR-0045). Default to frozen empties — placement
    // never creates shipments/documents; the repository assembles them onto the DETAIL read.
    shipments: Object.freeze([...(input.shipments ?? [])]),
    documents: Object.freeze([...(input.documents ?? [])]),
    // Activity log + Customs surfaces (UID-121 S7, ADR-0046). Same posture as B3's
    // shipments/documents: placement never writes them; the repository assembles them onto the
    // DETAIL read. Default to a frozen empty log / null customs.
    activityLog: Object.freeze([...(input.activityLog ?? [])]),
    customs: input.customs ?? null,
    // Requested-services placement snapshot (UID-428, ADR-0127). Same posture as B3's detail
    // surfaces: default to a frozen empty list; `placeOrder` populates it from the settling cart.
    requestedServices: Object.freeze([...(input.requestedServices ?? [])]),
    ...(input.createdAt !== undefined ? { createdAt: input.createdAt } : {}),
  })
}

/**
 * Fill the READ-ONLY inbound projection ids on an already-placed Order (UID-62).
 *
 * `netsuiteSalesOrderId` / `salesforceOpportunityId` are inbound projections of the
 * simulated NetSuite Sales Order + Salesforce Opportunity (DM§17 / §19.2, ADR-0007) —
 * NOT fields the customer or the checkout flow asserts. `placeOrder` mints them `null`;
 * the server-side projection step (the BFF `OrderService`, driven by the ACL stub ports)
 * calls this PURE helper to stamp the ids the ports minted. Immutable: the input order is
 * untouched, a NEW frozen Order is returned with every other field (lines, charges,
 * status, timestamps) preserved by reference.
 */
export function withOrderProjection(
  order: Order,
  projection: {
    readonly netsuiteSalesOrderId: string
    readonly salesforceOpportunityId: string
  },
): Order {
  invariant(
    typeof projection.netsuiteSalesOrderId === 'string' &&
      projection.netsuiteSalesOrderId.length > 0,
    'withOrderProjection: netsuiteSalesOrderId must be a non-empty string',
  )
  invariant(
    typeof projection.salesforceOpportunityId === 'string' &&
      projection.salesforceOpportunityId.length > 0,
    'withOrderProjection: salesforceOpportunityId must be a non-empty string',
  )
  return Object.freeze({
    ...order,
    netsuiteSalesOrderId: projection.netsuiteSalesOrderId,
    salesforceOpportunityId: projection.salesforceOpportunityId,
  })
}

/**
 * A minimal money shape for an order's computed total (ADR-0036 §1). Deliberately NOT
 * the {@link Price} value object — a total has no `basis`/`provenance`; it is only an
 * amount + currency. `amount` is a decimal string (money is exact; never a float).
 */
export interface OrderSummaryMoney {
  readonly amount: string
  readonly currency: string
}

/**
 * The lightweight orders-list projection (UID-63, ADR-0036 §1) — the list surface's
 * read-model, NOT the full {@link Order} aggregate. GET `/api/orders` returns an array
 * of these; the detail screen still fetches the full Order via GET `/api/orders/{id}`.
 * Frontend-local contract schema (`apps/api/openapi.yaml`), never an api-specs shared
 * schema (ADR-0036 §2).
 */
export interface OrderSummary {
  readonly id: string
  readonly status: OrderStatus
  readonly poReference: string | null
  readonly placedAt: string | null
  readonly createdAt: string
  /** Computed order total — see {@link toOrderSummary} for the rule. */
  readonly total: OrderSummaryMoney
  /** Count of {@link OrderLine}s on the order. */
  readonly lineCount: number
  /** Σ of every line's `quantity.value` (units ordered). */
  readonly unitCount: number
  /**
   * The synthesized human order name (UID-98 PR-A, gap analysis §B) — the first labelled
   * line's part name, or `null` when no line carries a label (the list then falls back to
   * the SO number via `formatOrderRef(id)`). Derived, NOT stored (no order-name column).
   */
  readonly orderName: string | null
  /**
   * The per-line part display-names, in line order (UID-98 PR-A) — the orders-list PARTS
   * column ("count + names"). Only lines that carry a `displayName` contribute; empty when
   * none do. `lineCount` remains the authoritative PARTS count.
   */
  readonly partNames: readonly string[]
  /**
   * The rolled-up order-level est-delivery (UID-98 PR-A, gap analysis §D) — the LATEST
   * per-line committed date (the order arrives when its last line does), or `null` when no
   * line carries one. Derived from the existing per-line promises; NO migration.
   */
  readonly estDeliveryDate: string | null
  /**
   * The orders-list "Payment type" cell (UID-132) — derived from the order's frozen
   * `payment` snapshot (ADR-0038 §1) via {@link derivePaymentDisplayLabel}, e.g.
   * "CC •••• 4242" / "ACH •••• 1234" / "PO #1234". `null` when the order carries no
   * payment snapshot (pre-checkout/pre-UID-60). Additive: no new stored column, no
   * migration — projected from data that already exists on the placed `Order`.
   */
  readonly paymentDisplayLabel: string | null
  /**
   * Up to `ORDER_SUMMARY_THUMBNAIL_CAP` (3) real part-thumbnail URLs for the row's part
   * swatch stack (UID-404, ADR-0126 D11/D12).
   *
   * `toOrderSummary` always returns `[]`: the thumbnail lives in the tenant Library
   * (`library.part.thumbnail_url`) and this projection is pure with no Library access. The
   * `GET /orders` route enriches it through a narrow, account-scoped read port in ONE
   * query per request (never per row) — see `order/part-thumbnail-read.ts`.
   *
   * NON-OPTIONAL HERE, OPTIONAL IN THE CONTRACT — deliberate, and the two are not in
   * conflict (Gate 5 W2). This is the SERVER's own read model, and the server always emits
   * the key (`[]` when nothing resolved), so requiring it here is the accurate type for
   * every producer and consumer inside `apps/api`. The openapi schema keeps the field OUT
   * of `required` for byte-compatibility with pre-UID-404 consumers, so the generated client
   * type is `string[] | undefined` and the web side must treat absent as `[]`. The server
   * guarantees MORE than the contract requires; that is the safe direction.
   *
   * DENSE AND FRONT-PACKED — POSITIONALLY UNRELATED TO {@link partNames}. The route walks
   * lines in line order but appends only RESOLVED URLs, so `thumbnailUrls[i]` corresponds to
   * neither line `i` nor `partNames[i]`; the two arrays are filtered on different predicates
   * (a line carrying a display label vs. a part having a Library image). Front-packing is
   * intentional — it is what lets a real image on line 8 reach swatch slot 1 — but it means
   * zipping the arrays for a tooltip or `alt` text would MISLABEL images.
   *
   * NOT A PARTS COUNT. `lineCount` remains the authoritative parts count and is what the
   * `+N` overflow chip derives from. A `thumbnailUrls` shorter than `lineCount` is the
   * NORMAL case — unresolvable sourceable ids, parts with a null thumbnail, values dropped
   * by the same-origin `/assets/` guard, and the cap of 3 all shorten it — so its length
   * carries no information the UI may act on.
   */
  readonly thumbnailUrls: readonly string[]
}

/**
 * Round a decimal-string amount × a (possibly non-integer) multiplier to integer cents,
 * skipping a non-finite result. Integer-cents accumulation avoids the float drift a naive
 * `+` over decimals introduces (mirrors the projection's `orderAmount`, but this is the
 * canonical order-total home for UID-63). Demo-scoped 2-decimal money; a malformed amount
 * contributes 0 rather than poisoning the whole sum with NaN.
 */
function amountToCents(amount: string, multiplier: number): number {
  const cents = Math.round(Number.parseFloat(amount) * multiplier * 100)
  return Number.isFinite(cents) ? cents : 0
}

/**
 * The minimal STRUCTURAL slice of an {@link Order} the total rule reads: each line's
 * priced amount × quantity, plus each order-level charge's flat amount. Deliberately a
 * subset (not the full aggregate) so the ONE canonical total rule can be applied to
 * either the shared {@link Order} OR the SPA's contract-generated `Order` — the latter
 * held by the order-detail screen, which has no precomputed total. One rule, one home
 * (ADR-0036 §1): a list total and a detail total for the same order cannot diverge.
 */
export interface OrderTotalInput {
  readonly lines: readonly {
    readonly price: { readonly amount: string; readonly currency: string }
    readonly quantity: { readonly value: number }
  }[]
  readonly charges?: readonly {
    readonly amount: { readonly amount: string; readonly currency: string }
  }[]
}

/**
 * The canonical Order total accumulated in INTEGER CENTS (the documented Order total
 * rule, ADR-0029): `Σ(line.price.amount × line.quantity.value) + Σ(charge.amount.amount)`
 * — charges included, not lines-only. Integer-cents accumulation avoids the float drift a
 * naive `+` over decimals introduces. This is the SINGLE home of the total rule; both
 * {@link toOrderSummary} (the list projection) and the SPA order-detail screen route
 * through it, so their totals can never diverge (ADR-0036 §1).
 */
export function computeOrderTotalCents(order: OrderTotalInput): number {
  let cents = 0
  for (const line of order.lines) {
    cents += amountToCents(line.price.amount, line.quantity.value)
  }
  for (const charge of order.charges ?? []) {
    cents += amountToCents(charge.amount.amount, 1)
  }
  return cents
}

/**
 * The canonical Order total as the {@link OrderSummaryMoney} shape `OrderSummary.total`
 * uses: {@link computeOrderTotalCents} formatted to a fixed 2-decimal string. Currency is
 * single-currency (USD demo, ADR-0036 §1) — the first line's price, else the first
 * charge, else `USD`; a mixed-currency order is out of demo scope. The ONE rule shared by
 * the list total and the detail-screen total.
 */
export function computeOrderTotal(order: OrderTotalInput): OrderSummaryMoney {
  const currency = order.lines[0]?.price.currency ?? order.charges?.[0]?.amount.currency ?? 'USD'
  return Object.freeze({ amount: (computeOrderTotalCents(order) / 100).toFixed(2), currency })
}

/**
 * Project a full {@link Order} down to its list-item {@link OrderSummary} (UID-63,
 * ADR-0036 §1). PURE. The order-total rule is NOT re-implemented here — it delegates to
 * the single canonical {@link computeOrderTotal} so the in-memory repo, the pg repo, and
 * the SPA detail screen can never diverge on it (ADR-0036 §1).
 *
 * `lineCount` counts lines; `unitCount` is Σ of every line's `quantity.value`. `placedAt`
 * is carried through (may be null).
 */
export function toOrderSummary(order: Order): OrderSummary {
  invariant(
    typeof order.createdAt === 'string' && order.createdAt.length > 0,
    'toOrderSummary: order.createdAt is required to summarize a persisted order',
  )
  let unitCount = 0
  const partNames: string[] = []
  for (const line of order.lines) {
    unitCount += line.quantity.value
    if (typeof line.displayName === 'string' && line.displayName.trim().length > 0) {
      partNames.push(line.displayName)
    }
  }
  return Object.freeze({
    id: order.id,
    status: order.status,
    poReference: order.poReference,
    placedAt: order.placedAt,
    createdAt: order.createdAt,
    total: computeOrderTotal(order),
    lineCount: order.lines.length,
    unitCount,
    // Derived list surfaces (UID-98 PR-A) — one home for the rule, reused across list/detail.
    orderName: deriveOrderName(order),
    partNames: Object.freeze(partNames),
    estDeliveryDate: deriveOrderEta(order),
    paymentDisplayLabel: derivePaymentDisplayLabel(order.payment),
    // UID-404 (ADR-0126 D12) — always the EMPTY array here, never absent. This projection
    // is pure and the order vertical has no Library access, so the part thumbnails are
    // joined on by the `GET /orders` route (one account-scoped Library read per request).
    // An empty array is the correct "no thumbnails" answer and the row renders its fallback
    // glyphs; the key is always present so the FE has no presence branch to get wrong.
    thumbnailUrls: Object.freeze([]) as readonly string[],
  })
}

/**
 * Mask an own-account carrier reference for a READ/wire response (UID-206, ADR-0067): keep only
 * the last-4, e.g. `"2258013433"` → `"••3433"`. Under UID-206 `own_account.accountRef` is now a
 * REAL tenant-entered carrier account number (it was a fixed demo constant before), so an
 * order-detail read must not surface it in full. The FULL ref stays frozen on the STORED Order
 * (records / the actual carrier hand-off) — only what leaves the API is masked, mirroring how the
 * payment snapshot keeps its token server-side and surfaces a display-only label. Matches the
 * checkout book's `maskShippingAccount` (`•• ` + last-4) so the two never diverge.
 */
export function maskOwnAccountRef(accountRef: string): string {
  return `••${accountRef.slice(-4)}`
}

/**
 * Prefix of the own-account "Shipping method" activity-log note written at placement (UID-224
 * item 2). Authored ONCE here so the server writer and the client retrieval-fallback parser share
 * a single source of truth — a format change can never silently break the FE parse (code-review
 * W2). The note is `${SHIPPING_METHOD_NOTE_PREFIX}${formatOwnAccountShippingLine(...)}`.
 */
export const SHIPPING_METHOD_NOTE_PREFIX = 'Shipping method: '

/**
 * The shared "Shipping method" display line for an OWN-ACCOUNT order (UID-224) — authored ONCE so
 * the placement activity-log note (server) and the Shipments-tab field (client) cannot drift
 * (code-review W2/S3). Carrier is the anchor: `Own account · <carrier> <maskedRef>`; the ref is
 * dropped when there is no carrier (a bare ref is meaningless), and the bare base `Own account`
 * is returned when there is no detail at all. `maskedAccountRef` MUST already be masked
 * (`••last4`) by the caller — this never masks (keeps the PCI mask at the write/read boundary).
 */
export function formatOwnAccountShippingLine(input: {
  carrier?: string | null
  maskedAccountRef?: string | null
}): string {
  const carrier = typeof input.carrier === 'string' ? input.carrier.trim() : ''
  const ref = typeof input.maskedAccountRef === 'string' ? input.maskedAccountRef.trim() : ''
  const parts = ['Own account']
  if (carrier.length > 0) {
    parts.push(ref.length > 0 ? `${carrier} ${ref}` : carrier)
  }
  return parts.join(' · ')
}

/**
 * Return a READ/wire copy of an order with its own-account shipping ref masked
 * ({@link maskOwnAccountRef}). `prepay`/`null` shipping is returned unchanged. Applied at the
 * `OrderDetail` response boundary (POST /orders, GET /orders/{id}, PATCH …/customs, POST
 * /orders/batch) so an order-detail read never surfaces the full carrier account number, while
 * the persisted aggregate keeps the real ref (UID-206). PURE — never mutates its input.
 */
export function toReadOrder(order: Order): Order {
  const method = order.shippingMethod
  if (method === null || method.kind !== 'own_account') {
    return order
  }
  return {
    ...order,
    shippingMethod: { ...method, accountRef: maskOwnAccountRef(method.accountRef) },
  }
}

/** The readiness partition of a cart's lines (DM§9 a′). */
export interface CartReadinessSplit {
  /** Lines carrying BOTH a Price and a DeliveryPromise — become OrderLines. */
  readonly ready: readonly OrderEligibleLineItem[]
  /** Lines still missing a Price and/or Promise — left in the cart / become a Quote. */
  readonly pending: readonly LineItem[]
}

/**
 * Partition a cart's lines by readiness (DM§9 a′) WITHOUT mutating the cart. Ready
 * IFF both Price and DeliveryPromise are present (the same discriminator the whole
 * codebase reads via `isOrderEligible`) — the caller never asserts readiness.
 */
export function splitCartByReadiness(cart: Cart): CartReadinessSplit {
  const ready: OrderEligibleLineItem[] = []
  const pending: LineItem[] = []
  for (const line of cart.lines) {
    if (isOrderEligible(line)) {
      ready.push(line)
    } else {
      pending.push(line)
    }
  }
  return { ready, pending }
}

/** The outcome of placing an order: the new Order + the pending lines left behind. */
export interface PlaceOrderResult {
  readonly order: Order
  /** The cart's pending lines — NOT ordered; the caller settles the cart with these. */
  readonly remainingLines: readonly LineItem[]
}

/**
 * Place an order from a cart (UID-61). PURE: the input cart is untouched; identity
 * (`id`, line ids) and the placement timestamp are supplied by the caller so the
 * domain holds no I/O or clock. Splits by readiness, freezes each ready line as an
 * OrderLine baseline, carries the cart's PO reference + charges + account onto the
 * order, and returns the pending lines for the caller to settle the cart with.
 * Rejects with {@link OrderError} `no_ready_lines` when there is nothing to place.
 */
export function placeOrder(input: {
  cart: Cart
  id: string
  mintLineId: () => string
  placedAt: string
  createdAt?: string
  /**
   * The frozen checkout-placement snapshot (UID-60, ADR-0038 §1), threaded onto the new
   * Order. Optional so pre-UID-60 callers (and the pure UID-61 tests) are unchanged; the
   * BFF `OrderService` supplies it after its terms + eligibility precheck, stamping
   * `termsAcceptedAt` from its OWN clock (never client-supplied).
   */
  checkout?: {
    shippingAddress: ShippingAddress
    shippingMethod: ShippingMethod
    payment: PaymentSelection
    termsAcceptedAt: string
  }
}): PlaceOrderResult {
  const { ready, pending } = splitCartByReadiness(input.cart)
  if (ready.length === 0) {
    throw new OrderError(
      'no_ready_lines',
      'The cart has no ready (priced + promised) lines to place.',
    )
  }
  const lines = ready.map((line) => makeOrderLine({ id: input.mintLineId(), line }))
  const order = makeOrder({
    id: input.id,
    accountId: input.cart.accountId,
    status: 'placed',
    lines,
    poReference: input.cart.poReference,
    placedAt: input.placedAt,
    charges: input.cart.charges,
    // Snapshot the cart's requested value-added services onto the order (UID-428, ADR-0127) —
    // the SAME domain source the `requestedServicesNote(...)` activity entry projects from, so
    // the structured capture and the human-readable note never diverge. Defaults to [] via
    // `makeOrder` when the cart requested none.
    requestedServices: input.cart.requestedServices ?? [],
    ...(input.createdAt !== undefined ? { createdAt: input.createdAt } : {}),
    ...(input.checkout !== undefined
      ? {
          shippingAddress: input.checkout.shippingAddress,
          shippingMethod: input.checkout.shippingMethod,
          payment: input.checkout.payment,
          termsAcceptedAt: input.checkout.termsAcceptedAt,
        }
      : {}),
  })
  return { order, remainingLines: pending }
}
